import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sword, Shield, Wind, BookOpen, Sparkles, Plus, Brain } from "lucide-react";
import TitlesPanel from "@/components/TitlesPanel";
import EquippedPanel from "@/components/EquippedPanel";

const STAT_META = {
  strength:      { class: "Warrior",  Icon: Sword,    hintKey: "character.hpTip" },
  defense:       { class: "Guardian", Icon: Shield,   hintKey: "character.defTip" },
  dexterity:     { class: "Rogue",    Icon: Wind,     hintKey: "character.dexTip" },
  intelligence:  { class: "Wizard",   Icon: BookOpen, hintKey: "character.intTip" },
  charisma:      { class: "Bard",     Icon: Sparkles, hintKey: "character.chaTip" },
  concentration: { class: "All",      Icon: Brain,    hintKey: "character.conTip" },
};

export default function CharacterPanel({ ch, setCh }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);

  useEffect(() => { api.jobs().then(setJobs); }, []);

  const allocate = async (stat) => {
    if (busy || ch.stat_points <= 0) return;
    setBusy(true);
    try {
      const data = await api.allocate(stat, 1);
      setCh(data);
      toast(`+1 ${stat}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally { setBusy(false); }
  };

  const chooseJob = async (job_id) => {
    setBusy(true);
    try {
      const data = await api.setJob(job_id);
      setCh(data);
      toast(job_id ? "Job started" : "Job quit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("character.class")}</div>
            <div className="font-heading text-2xl text-[#E2DFD8]" data-testid="character-class">{ch.class}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("character.level")}</div>
            <div className="font-mono text-2xl text-[#E55A00]" data-testid="character-level">{ch.level}</div>
          </div>
        </div>
        {ch.stat_points > 0 && (
          <div className="mt-2 px-2 py-1 border border-[#B89C3A] bg-[#1a1610]" data-testid="stat-points-badge">
            <span className="font-heading text-xs tracking-widest text-[#E5C34A] uppercase">
              {t("character.statPoints", { count: ch.stat_points })}
            </span>
          </div>
        )}
      </div>

      <EquippedPanel ch={ch} setCh={setCh} />

      <div className="grid grid-cols-2 gap-2">
        {Object.entries(STAT_META).map(([stat, meta]) => {          const Icon = meta.Icon;
          const bonus = (ch.skill_bonuses || {})[stat] || 0;
          const base = (ch.stats?.[stat] || 0) - bonus;
          return (
            <div key={stat} className="panel p-3 relative" data-testid={`stat-${stat}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} />
                <span className="font-heading text-[10px] uppercase tracking-widest text-[#8c8c8c]">
                  {stat}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-1">
                  <span className="font-mono text-2xl text-[#E2DFD8]">{ch.stats[stat]}</span>
                  {bonus > 0 && (
                    <span
                      className="font-mono text-[10px] text-[#B89C3A]"
                      data-testid={`stat-bonus-${stat}`}
                      title={`Base ${base} + Skills +${bonus}`}
                    >
                      (+{bonus})
                    </span>
                  )}
                </div>
                {ch.stat_points > 0 && (
                  <button
                    data-testid={`allocate-${stat}`}
                    onClick={() => allocate(stat)}
                    disabled={busy}
                    className="w-6 h-6 border border-[#333] hover:border-[#E55A00] flex items-center justify-center"
                  >
                    <Plus className="w-3 h-3 text-[#E55A00]" strokeWidth={2} />
                  </button>
                )}
              </div>
              <div className="text-[10px] text-[#666] mt-1 italic">{t(meta.hintKey)}</div>
              <div className="text-[9px] font-heading tracking-widest text-[#4A7BC7] uppercase mt-1">
                → {meta.class}
              </div>
            </div>
          );
        })}
      </div>

      <div className="panel p-3">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2">{t("character.jobs")}</div>
        <div className="space-y-2">
          {jobs.map(j => {
            const locked = ch.level < j.requires_level;
            const active = ch.job === j.id;
            return (
              <div key={j.id} className={`panel-inner p-2 flex items-center justify-between ${active ? "border-[#E55A00]" : ""}`} data-testid={`job-${j.id}`}>
                <div>
                  <div className="font-heading text-sm text-[#E2DFD8]">{j.name}</div>
                  <div className="font-mono text-[10px] text-[#8c8c8c]">
                    {j.gold_per_hr}g/hr · {j.xp_per_hr}xp/hr · req lv{j.requires_level}
                  </div>
                </div>
                <button
                  disabled={locked || busy}
                  onClick={() => chooseJob(active ? null : j.id)}
                  className="px-3 py-1 border border-[#333] hover:border-[#E55A00] text-[10px] font-heading tracking-widest uppercase disabled:opacity-30"
                >
                  {locked ? t("character.locked") : active ? t("character.quit") : t("character.work")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <TitlesPanel ch={ch} setCh={setCh} />
    </div>
  );
}
